/**
 * Proposte della ricerca nella lista chat:
 * - nessun risultato e il testo e' un numero → "Scrivi a +39 ...";
 * - Lead/Clienti trovati per nome che non hanno ancora una chat → "Scrivi a <nome>".
 * Il clic avvia il flusso della Nuova chat (scelta del numero aziendale e verifica).
 */
import { MessageSquare, Phone, User } from 'lucide-react';
import { normalizeWhatsAppDigits } from '@/lib/services/whatsapp/phoneDigits';
import { formatPhoneForDisplay, looksLikePhoneNumber } from '@/lib/services/whatsapp/chatListRules';
import type { ClientChatCandidate } from '@/lib/services/whatsapp/clientPhoneSearch';
import type { NewChatTarget } from './NewChatModal';

interface Props {
  /** Presente solo come stato vuoto della ricerca. */
  query?: string;
  clients: ClientChatCandidate[];
  onPick: (target: NewChatTarget) => void;
}

const rowCls = 'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left hover:bg-[rgba(55,53,47,0.04)] dark:hover:bg-white/5';

export function SearchSuggestions({ query, clients, onPick }: Props) {
  const digits = query && looksLikePhoneNumber(query) ? normalizeWhatsAppDigits(query) : '';

  return (
    <div className="flex-shrink-0 px-2 pt-2">
      {query !== undefined && (
        <div className="flex flex-col items-center py-6 px-4 text-center">
          <div className="w-10 h-10 rounded-full bg-[rgba(55,53,47,0.08)] dark:bg-white/10 flex items-center justify-center mb-2">
            <MessageSquare className="w-5 h-5 text-[#787774]" />
          </div>
          <p className="text-sm text-[#787774]">{digits ? 'Nessuna chat con questo numero' : 'Nessuna chat trovata'}</p>
        </div>
      )}
      {digits.length >= 8 && (
        <button type="button" className={rowCls} onClick={() => onPick({ digits, name: null })}>
          <span className="w-9 h-9 rounded-full bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center flex-shrink-0">
            <Phone className="w-4 h-4 text-emerald-600" />
          </span>
          <span className="text-sm text-[#37352F] dark:text-white">Scrivi a <strong>{formatPhoneForDisplay(digits)}</strong></span>
        </button>
      )}
      {clients.length > 0 && (
        <div className="pb-1 border-b border-[rgba(55,53,47,0.09)] dark:border-white/10">
          <p className="px-3 pt-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-[#787774]">Lead e Clienti senza chat</p>
          {clients.slice(0, 5).map((c) => (
            <button key={c.id} type="button" className={rowCls} onClick={() => onPick({ digits: c.numbers[0], name: c.name })}>
              <span className="w-9 h-9 rounded-full bg-[rgba(35,131,226,0.08)] flex items-center justify-center flex-shrink-0">
                <User className="w-4 h-4 text-[#2383E2]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm text-[#37352F] dark:text-white">Scrivi a {c.name}</span>
                <span className="block truncate text-xs text-[#787774]">{c.kind} · {formatPhoneForDisplay(c.numbers[0])}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
